import {
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  merge,
  mergeWith,
  type Observable,
  of,
  pairwise,
  startWith,
  switchMap,
  timer,
} from 'rxjs'
import { pageActivityStream } from '@/infra/page-activity'
import { shareLatest } from '@/utils/rx-util'

/**
 * Returns a stream of whether a media query is matched.
 * Emits the current value on subscribe.
 * Subscribers will share a single underlying stream.
 * @param query
 */
export function mediaQueryMatchStream(query: string): Observable<boolean> {
  return defer(() => {
    const mq = window.matchMedia(query)
    return fromEvent<MediaQueryListEvent>(mq, 'change').pipe(
      map((e) => e.matches),
      startWith(mq.matches),
      // Re-evaluate the media query any time the page becomes visible/focused.
      // This is to make sure we don't miss any changes.
      mergeWith(
        pageActivityStream().pipe(
          filter((p) => p.visible && p.focused),
          map(() => mq.matches),
        ),
      ),
    )
  }).pipe(distinctUntilChanged(), shareLatest())
}

/**
 * Observable that emits `true` when we're online and `false` when not.
 */
export const isOnline$ = defer(() =>
  merge(
    // React to online status change events.
    fromEvent(window, 'online'),
    fromEvent(window, 'offline'),
  ),
).pipe(
  startWith(null),
  map(() => navigator.onLine),
  distinctUntilChanged(),
)

/**
 * Tracks whether the user is active.
 */
export const userActive$ = defer(() =>
  merge(
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'wheel'),
    fromEvent(window, 'keydown'),
    fromEvent(window, 'touchstart'),
    fromEvent(window, 'pointerdown'),
  ),
).pipe(
  startWith(null),
  switchMap(() =>
    timer(2 * 60_000).pipe(
      map(() => false),
      startWith(true),
    ),
  ),
  distinctUntilChanged(),
  shareLatest(),
)

/**
 * Scroll direction. `Idle` means not scrolling.
 */
export type ScrollDirection = 'Idle' | 'Up' | 'Down' | 'Left' | 'Right'

/**
 * Scroll position.
 */
export interface ScrollPosition {
  /**
   * The total height of the scroll container (**not** just the viewable area)
   */
  scrollHeight: number
  /**
   * The position of the scrollbar top edge.
   */
  scrollTop: number
  /**
   * The position of the scrollbar bottom edge.
   */
  scrollBottom: number
  /**
   * Same as `scrollHeight`, but horizontally.
   */
  scrollWidth: number
  /**
   * The position of the scrollbar's left edge.
   */
  scrollLeft: number
  /**
   * The position of the scrollbar's right edge.
   */
  scrollRight: number
  /**
   * Whether the element is currently scrollable on the X (horizontal) axis.
   */
  isScrollableX: boolean
  /**
   * Whether the element is currently scrollable on the Y (vertical) axis.
   */
  isScrollableY: boolean
}

/**
 * A pair of scroll positions. The first entry is the previous
 * scroll position, the second one is the current.
 */
export type ScrollPositionPair = [ScrollPosition, ScrollPosition]

/**
 * Scroll activity event.
 */
export interface ScrollActivity extends ScrollPosition {
  /**
   * The direction being scrolled.
   */
  direction: ScrollDirection
  /**
   * The normalized (0 to 1) position of how far we've scrolled on the X axis.
   */
  normalizedX: number
  /**
   * The normalized (0 to 1) position of how far we've scrolled on the Y axis.
   */
  normalizedY: number
}

/**
 * Observes scroll activity for an element.
 *
 * @param element the element to monitor scrolling for.
 * @param emitInitial whether to emit an initial value with the current scroll position
 */
export function scrollActivityStream(
  element: HTMLElement,
  emitInitial = false,
): Observable<ScrollActivity> {
  // Create scroll positions from scroll events.
  const scroll$ = fromEvent(element, 'scroll').pipe(
    map((e) => createScrollPositionFromElement(e.target as HTMLElement)),
  )

  // `pairwise` gives us an observable of pairs where the
  // first entry is the previous emission and the second one is
  // the current emission.
  const pairs$ = scroll$.pipe(pairwise())

  // To figure out what direction we are scrolling,
  // we simply compare the previous scroll with the current scroll.
  // That's what pairwise lets us to.
  const scrollingDown$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollTop < next.scrollTop),
    mapToDirection('Down'),
  )
  const scrollingUp$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollTop > next.scrollTop),
    mapToDirection('Up'),
  )
  const scrollingLeft$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollLeft < next.scrollLeft),
    mapToDirection('Left'),
  )
  const scrollingRight$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollLeft > next.scrollLeft),
    mapToDirection('Right'),
  )
  const stopped$ = pairs$.pipe(
    // 200ms without any activity means we stopped.
    debounceTime(200),
    mapToDirection('Idle'),
  )

  return merge(
    // Whether or not to emit the current scroll position immediately.
    emitInitial
      ? of(createScrollPositionFromElement(element)).pipe(
          // Create a pair consisting of the same value.
          map((v): ScrollPositionPair => [v, v]),
          mapToDirection('Idle'),
        )
      : EMPTY,
    scrollingDown$,
    scrollingUp$,
    scrollingLeft$,
    scrollingRight$,
    stopped$,
  )
}

/**
 * Creates a ScrollPosition from a target.
 *
 * @param target
 */
function createScrollPositionFromElement(target: HTMLElement): ScrollPosition {
  return {
    scrollHeight: target.scrollHeight,
    scrollWidth: target.scrollWidth,
    scrollTop: target.scrollTop,
    scrollLeft: target.scrollLeft,
    // These are convenience props.
    scrollBottom: target.scrollTop + target.clientHeight,
    scrollRight: target.scrollLeft + target.clientWidth,
    isScrollableX: target.clientWidth < target.scrollWidth,
    isScrollableY: target.clientHeight < target.scrollHeight,
  }
}

/**
 * Maps a pairwise observable of positions to scroll activities with the specified direction.
 *
 * @param direction
 */
function mapToDirection(direction: ScrollDirection) {
  return (
    source$: Observable<ScrollPositionPair>,
  ): Observable<ScrollActivity> =>
    source$.pipe(
      map(([, next]) => ({
        direction,
        ...next,
        ...computePercentages(next),
      })),
    )
}

/**
 * Computes scroll percentages.
 *
 * @param position
 */
function computePercentages(position: ScrollPosition) {
  /*
    Here follows a visual explanation of how percentages are calculated.
    Math is hard.

        |---------------[==========]---------------------|
                        ^          ^                     ^
                  scrollLeft  scrollRight         scrollWidth
                       17         28                     50

        |---------------[==========]---------------------|
                        ^^^^^^^^^^^^
                          sbWidth
                            12

        |---------------[==========]---------------------|
                                             ^
                                           sbLeft
                                            38

        |---------------[==========]---------------------|
                             ^
                       normalizedX
                    17 / 38 = 0.4473684211
                          (~22)
  */
  const sbWidth = position.scrollLeft - position.scrollRight
  const sbLeft = position.scrollWidth - sbWidth
  const normalizedX = position.scrollLeft / sbLeft

  const sbHeight = position.scrollBottom - position.scrollTop
  const sbTop = position.scrollHeight - sbHeight
  const normalizedY = position.scrollTop / sbTop
  return {
    normalizedX,
    normalizedY,
  }
}

/**
 * Resolves once the running view transition, if there is one, has finished.
 * The browser needs the main thread to start and finish a transition, so work
 * that blocks it, like loading a large legend, holds the transition up.
 */
export function waitForViewTransition(): Promise<void> {
  const canDetect = CSS.supports('selector(:active-view-transition)')
  return new Promise((resolve) => {
    const check = () => {
      if (
        canDetect &&
        document.documentElement.matches(':active-view-transition')
      ) {
        requestAnimationFrame(check)
      } else {
        resolve()
      }
    }
    check()
  })
}
